/**
 * Inventario y precios de todo un evento (rol 11): lo que la página de
 * inventario muestra, cómo filtra y agrupa, qué cambió respecto a lo leído y
 * cuánto se puede trasladar. Funciones puras: nada de Angular ni de
 * Firestore; el servicio lee y escribe, aquí solo se calcula.
 */
import { normalizeText } from '../product-import/product-import.model';

/** Zona de primer nivel del evento (lo que ve la caja como barra). */
export interface InventoryZone {
    key: string;
    name: string;
}

/** Una presentación tal como está guardada: lo que se edita. */
export interface InventoryPresentation {
    key: string;
    /** "Botella 750ML", "Vaso", "Unidad". */
    name: string;
    price: number;
    inventory: number;
    visible: boolean;
    main: boolean;
}

/** Un producto vivo del evento con sus presentaciones. */
export interface InventoryProduct {
    key: string;
    name: string;
    /** Zona de primer nivel a la que pertenece (lo que cuelga de una sub-zona se atribuye a su zona madre). */
    zoneKey: string;
    zoneName: string;
    /** Categoría real del documento (la zona o sub-zona donde vive). */
    categoryKey: string;
    section: string;
    code: string;
    supplier: string;
    image: string;
    /** `visible` del documento del producto (lo que apaga el monitor). */
    visible: boolean;
    kitchen: boolean;
    variants: string[];
    presentations: InventoryPresentation[];
}

export interface EventInventory {
    zones: InventoryZone[];
    products: InventoryProduct[];
}

/** Una fila de la tabla: producto + una de sus presentaciones. */
export interface InventoryRow {
    id: string;
    product: InventoryProduct;
    presentation: InventoryPresentation;
    /** Lo que vale la fila; se compara con la presentación para saber si cambió. */
    price: number;
    inventory: number;
    visible: boolean;
    /**
     * Lo que hay escrito en los campos. Va aparte del número para que al
     * teclear "8." no se reescriba como "8.00" a medio camino; el número
     * sigue al último texto válido y al salir del campo el texto se ordena.
     */
    priceText: string;
    inventoryText: string;
}

export function priceText(price: number): string {
    return Number(price || 0).toFixed(2);
}

/** Cambio pendiente de una fila (solo los campos que cambiaron). */
export interface InventoryEdit {
    brickKey: string;
    productName: string;
    presentationKey: string;
    presentationName: string;
    price?: number;
    inventory?: number;
    visible?: boolean;
    /** Valores anteriores, para el historial. */
    before: { price: number; inventory: number; visible: boolean };
}

/** Existencias por debajo de esto cuentan como "bajas". */
export const LOW_STOCK = 5;

export interface InventoryFilter {
    search: string;
    /** '' = todas las zonas. */
    zoneKey: string;
    /** 'all' | 'low' (≤ LOW_STOCK, incluye agotados) | 'out' (0) | 'hidden' */
    stock: 'all' | 'low' | 'out' | 'hidden';
}

export const EMPTY_FILTER: InventoryFilter = { search: '', zoneKey: '', stock: 'all' };

/** Precio o inventario tal como se escribe: acepta "8,50", "$8.50", " 12 "; null si no es un número válido. */
export function parseAmount(value: any): number | null {
    const clean = String(value === null || value === undefined ? '' : value)
        .trim()
        .replace(/^[^\d.,-]+/, '')
        .replace(/\s+/g, '')
        .replace(',', '.');
    if (!clean) return null;
    const num = Number(clean);
    return isFinite(num) && num >= 0 ? num : null;
}

/** El inventario es entero; "12.0" vale, "12.5" no. */
export function parseUnits(value: any): number | null {
    const num = parseAmount(value);
    if (num === null) return null;
    return Number.isInteger(num) ? num : null;
}

/** Una fila por presentación, con los valores editables copiados de lo leído. */
export function buildRows(products: InventoryProduct[]): InventoryRow[] {
    const rows: InventoryRow[] = [];
    products.forEach(product => {
        product.presentations.forEach(presentation => {
            rows.push({
                id: `${product.key}/${presentation.key}`,
                product,
                presentation,
                price: presentation.price,
                inventory: presentation.inventory,
                visible: presentation.visible,
                priceText: priceText(presentation.price),
                inventoryText: String(presentation.inventory),
            });
        });
    });
    return rows;
}

export function isLowStock(inventory: number): boolean {
    return Number(inventory || 0) <= LOW_STOCK;
}

export function isOutOfStock(inventory: number): boolean {
    return Number(inventory || 0) <= 0;
}

/** Oculto para la caja: el producto apagado desde el monitor o la presentación apagada. */
export function isHiddenRow(row: InventoryRow): boolean {
    return row.product.visible === false || row.visible === false;
}

/** Cambió respecto a lo leído. */
export function isDirty(row: InventoryRow): boolean {
    return row.price !== row.presentation.price
        || row.inventory !== row.presentation.inventory
        || row.visible !== row.presentation.visible;
}

function matchesSearch(row: InventoryRow, term: string): boolean {
    if (!term) return true;
    const words = term.split(/\s+/).filter(w => w.length > 0);
    const haystack = normalizeText([
        row.product.name, row.presentation.name, row.product.code, row.product.supplier, row.product.section,
        row.product.zoneName, (row.product.variants || []).join(' '),
    ].join(' '));
    return words.every(w => haystack.indexOf(w) >= 0);
}

/**
 * Filtra sin perder el orden. El filtro de existencias mira lo guardado (lo
 * que la caja ve ahora), no lo que se está escribiendo: una fila que se está
 * corrigiendo no desaparece de la vista a medio teclear.
 */
export function filterRows(rows: InventoryRow[], filter: InventoryFilter): InventoryRow[] {
    const term = normalizeText(filter.search);
    return rows.filter(row => {
        if (filter.zoneKey && row.product.zoneKey !== filter.zoneKey) return false;
        if (filter.stock === 'low' && !isLowStock(row.presentation.inventory)) return false;
        if (filter.stock === 'out' && !isOutOfStock(row.presentation.inventory)) return false;
        if (filter.stock === 'hidden' && !(row.product.visible === false || row.presentation.visible === false)) return false;
        return matchesSearch(row, term);
    });
}

export interface ZoneGroup {
    zone: InventoryZone;
    rows: InventoryRow[];
    units: number;
    low: number;
    out: number;
}

/** Filas agrupadas por zona, en el orden de las zonas del evento; una zona sin filas no aparece. */
export function groupByZone(rows: InventoryRow[], zones: InventoryZone[]): ZoneGroup[] {
    const byZone: { [key: string]: ZoneGroup } = {};
    const order: InventoryZone[] = zones.slice();
    rows.forEach(row => {
        const key = row.product.zoneKey || '';
        if (!byZone[key]) {
            let zone = order.filter(z => z.key === key)[0];
            if (!zone) {
                zone = { key, name: row.product.zoneName || 'Sin zona' };
                order.push(zone);
            }
            byZone[key] = { zone, rows: [], units: 0, low: 0, out: 0 };
        }
        const group = byZone[key];
        group.rows.push(row);
        group.units += Number(row.presentation.inventory || 0);
        if (isOutOfStock(row.presentation.inventory)) group.out++;
        else if (isLowStock(row.presentation.inventory)) group.low++;
    });
    return order.filter(z => !!byZone[z.key]).map(z => byZone[z.key]);
}

/** Resumen por zona sobre TODAS las filas (sin filtro), para las tarjetas de arriba. */
export interface ZoneSummary {
    zone: InventoryZone;
    products: number;
    units: number;
    low: number;
    out: number;
}

export function summarizeZones(rows: InventoryRow[], zones: InventoryZone[]): ZoneSummary[] {
    return groupByZone(rows, zones).map(group => ({
        zone: group.zone,
        products: group.rows.length,
        units: group.units,
        low: group.low,
        out: group.out,
    }));
}

/** Los cambios pendientes de todas las filas, listos para guardar. */
export function collectEdits(rows: InventoryRow[]): InventoryEdit[] {
    return rows.filter(isDirty).map(row => {
        const edit: InventoryEdit = {
            brickKey: row.product.key,
            productName: row.product.name,
            presentationKey: row.presentation.key,
            presentationName: row.presentation.name,
            before: { price: row.presentation.price, inventory: row.presentation.inventory, visible: row.presentation.visible },
        };
        if (row.price !== row.presentation.price) edit.price = row.price;
        if (row.inventory !== row.presentation.inventory) edit.inventory = row.inventory;
        if (row.visible !== row.presentation.visible) edit.visible = row.visible;
        return edit;
    });
}

/**
 * Visibilidad del producto tras aplicar los cambios de sus filas: visible si
 * alguna presentación queda visible (la misma regla con la que la caja lo
 * apaga al agotarse). Solo cambia cuando alguna fila del producto cambió.
 */
export function productVisibleAfter(product: InventoryProduct, rows: InventoryRow[]): boolean | null {
    const own = rows.filter(r => r.product.key === product.key);
    if (!own.some(isDirty)) return null;
    return own.some(r => r.visible);
}

/** Lo escrito en el precio: el número sigue al último texto válido. */
export function typePrice(row: InventoryRow, text: any) {
    row.priceText = String(text === null || text === undefined ? '' : text);
    const price = parseAmount(row.priceText);
    if (price !== null) row.price = price;
}

/** Lo escrito en el inventario: el número sigue al último texto válido (entero). */
export function typeInventory(row: InventoryRow, text: any) {
    row.inventoryText = String(text === null || text === undefined ? '' : text);
    const units = parseUnits(row.inventoryText);
    if (units !== null) row.inventory = units;
}

/** Al salir del campo, el texto se ordena según el número (lo inválido se descarta). */
export function tidyRow(row: InventoryRow) {
    row.priceText = priceText(row.price);
    row.inventoryText = String(row.inventory);
}

/** −/+ de una en una; el inventario nunca baja de 0. */
export function stepRowInventory(row: InventoryRow, delta: number) {
    row.inventory = Math.max(0, Number(row.inventory || 0) + delta);
    row.inventoryText = String(row.inventory);
}

/** Vuelve la fila a lo leído. */
export function resetRow(row: InventoryRow) {
    row.price = row.presentation.price;
    row.inventory = row.presentation.inventory;
    row.visible = row.presentation.visible;
    tidyRow(row);
}

/** Tras guardar: lo escrito pasa a ser lo leído. */
export function commitRow(row: InventoryRow) {
    row.presentation.price = row.price;
    row.presentation.inventory = row.inventory;
    row.presentation.visible = row.visible;
    tidyRow(row);
}

/** Existencias nuevas que vienen de fuera (un traslado): fila y presentación a la vez. */
export function setRowInventory(row: InventoryRow, inventory: number) {
    row.presentation.inventory = inventory;
    row.inventory = inventory;
    row.inventoryText = String(inventory);
}

// ─── Traslados ─────────────────────────────────────────────────────────────

/** Producto en la zona destino con el mismo nombre (misma regla que copiar e importar). */
export function findSameProduct(products: InventoryProduct[], name: string, zoneKey: string, exceptKey: string = ''): InventoryProduct | null {
    const target = normalizeText(name);
    const found = products.filter(p => p.zoneKey === zoneKey && p.key !== exceptKey && normalizeText(p.name) === target);
    return found.length > 0 ? found[0] : null;
}

/** La presentación destino: la que se llama igual que la de origen, o la principal, o la primera. */
export function matchPresentation(product: InventoryProduct, presentationName: string): InventoryPresentation | null {
    const target = normalizeText(presentationName);
    return product.presentations.filter(p => normalizeText(p.name) === target)[0]
        || product.presentations.filter(p => p.main)[0]
        || product.presentations[0]
        || null;
}

/** Cantidad válida para trasladar: entero entre 1 y lo disponible. */
export function transferIssue(quantity: any, available: number): string {
    const units = parseUnits(quantity);
    if (units === null) return 'Escribe una cantidad entera.';
    if (units < 1) return 'La cantidad tiene que ser al menos 1.';
    if (units > available) return `Solo hay ${available} ${available === 1 ? 'unidad' : 'unidades'} en la zona de origen.`;
    return '';
}

/** Registro de un traslado, tal como queda en events/{key}/inventory_transfers. */
export interface InventoryTransferLog {
    key?: string;
    date: Date | null;
    user: string;
    productName: string;
    presentationName: string;
    quantity: number;
    fromZoneKey: string;
    fromZoneName: string;
    toZoneKey: string;
    toZoneName: string;
    /** Se creó el producto en el destino porque no existía. */
    created: boolean;
    fromBefore: number;
    fromAfter: number;
    toBefore: number;
    toAfter: number;
}

/** Lo que devuelve un traslado: el registro y las llaves tocadas, para reflejarlo en pantalla sin releer. */
export interface InventoryTransferResult {
    log: InventoryTransferLog;
    fromBrickKey: string;
    fromPresentationKey: string;
    toBrickKey: string;
    toPresentationKey: string;
}

/** "hace 3 min", "hace 1 h", "ahora". */
export function agoLabel(from: Date | null, now: Date): string {
    if (!from) return '';
    const minutes = Math.max(0, Math.floor((now.getTime() - from.getTime()) / 60000));
    if (minutes <= 0) return 'ahora';
    if (minutes < 60) return `hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `hace ${days} ${days === 1 ? 'día' : 'días'}`;
}
